
import React, { useState } from 'react';
import { X, Check, Loader2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { ModalType } from '../types';

// 제공해주신 Google Apps Script 및 Discord 웹훅 URL
const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbz7Vu-W_tg8kfzh17EuF6Zai2LECNGasM3TJvgozwWLx1irBJ9vpn6hGtg6q_sycmhS/exec"; 
const DISCORD_WEBHOOK_URL = "https://discord.com/api/webhooks/1460904355157905573/SBset13dXNKDhAqv_6gdXHxkw4qLluTNVaay9bKkQzIvZ6xJce9MvGC_e6Z63Y9y9GER";

interface ApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ApplicationModal: React.FC<ApplicationModalProps> = ({ isOpen, onClose }) => {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    agreePrivacy: false,
    agreeTerms: false,
    agreeMarketing: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.agreePrivacy || !formData.agreeTerms) {
      alert('필수 약관에 동의해주세요.');
      return;
    }

    if (!APPS_SCRIPT_URL) {
      alert('설정된 Google Apps Script URL이 없습니다.');
      return;
    }

    setStatus('submitting');
    const timestamp = new Date().toLocaleString();

    try {
      // 1. 구글 스프레드시트에 데이터 적재
      // 'no-cors' 모드는 응답을 읽을 수 없으나 데이터 전송은 정상적으로 이루어집니다.
      const sheetPromise = fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        cache: 'no-cache',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, timestamp }),
      });

      // 2. 디스코드 채널로 알림 전송 (이 서버/클라이언트에서 직접 전송)
      const discordPayload = {
        embeds: [{
          title: "📢 새로운 17기 지원서 접수",
          description: "ENACTUS YM 17기 신입 부원 지원서가 방금 도착했습니다.",
          color: 16761378, // #FFC222 (Hex to Decimal)
          fields: [
            { name: "👤 성함", value: formData.name, inline: true },
            { name: "📞 연락처", value: formData.phone, inline: true },
            { name: "🕒 신청일시", value: timestamp, inline: false },
            { 
              name: "✅ 동의 현황", 
              value: `개인정보: ${formData.agreePrivacy ? '동의' : '미동의'}\n이용약관: ${formData.agreeTerms ? '동의' : '미동의'}\n마케팅: ${formData.agreeMarketing ? '동의' : '미동의'}`,
              inline: false 
            }
          ],
          footer: { text: "ENACTUS YM Recruitment System" },
          timestamp: new Date().toISOString()
        }]
      };

      const discordPromise = fetch(DISCORD_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(discordPayload)
      });

      // 두 작업이 모두 시작된 후 시트 전송이 완료되면 성공 처리
      await sheetPromise;
      // 디스코드 전송은 백그라운드에서 처리되어도 무방하지만, 안정성을 위해 대기
      try { await discordPromise; } catch (dErr) { console.error("Discord send failed", dErr); }

      setStatus('success');
      
      setTimeout(() => {
        setStatus('idle');
        setFormData({
          name: '',
          phone: '',
          agreePrivacy: false,
          agreeTerms: false,
          agreeMarketing: false
        });
        onClose();
      }, 2500);

    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const allAgreed = formData.agreePrivacy && formData.agreeTerms && formData.agreeMarketing;
  
  const handleToggleAll = () => {
    const nextState = !allAgreed;
    setFormData(prev => ({
      ...prev,
      agreePrivacy: nextState,
      agreeTerms: nextState,
      agreeMarketing: nextState
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/95 backdrop-blur-sm"
        onClick={status === 'submitting' ? undefined : onClose}
      />
      <motion.div 
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        className="relative w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-3xl p-8 md:p-10 shadow-2xl overflow-hidden"
      >
        {status !== 'submitting' && status !== 'success' && (
          <button onClick={onClose} className="absolute top-6 right-6 text-zinc-500 hover:text-white transition-colors">
            <X size={24} />
          </button>
        )}

        <div className="mb-8">
          <h3 className="text-2xl font-black mb-2">ENACTUS YM 17기 지원하기</h3>
          <p className="text-zinc-500 text-sm">작성하신 정보는 선발 과정 외에 사용되지 않습니다.</p>
        </div>

        {status === 'success' ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-12 text-center space-y-4"
          >
            <div className="w-20 h-20 bg-[#FFC222] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#FFC222]/20">
              <Check size={40} className="text-black" />
            </div>
            <h4 className="text-2xl font-bold text-white">신청이 완료되었습니다!</h4>
            <p className="text-zinc-400">데이터가 스프레드시트에 저장되었으며,<br/>운영진에게 알림이 전송되었습니다. 곧 연락드릴게요!</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">이름</label>
              <input 
                required
                disabled={status === 'submitting'}
                type="text" 
                placeholder="성함을 입력해주세요"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3.5 focus:outline-none focus:border-[#FFC222] transition-colors disabled:opacity-50 text-white"
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">휴대폰 번호</label>
              <input 
                required
                disabled={status === 'submitting'}
                type="tel" 
                placeholder="010-0000-0000"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3.5 focus:outline-none focus:border-[#FFC222] transition-colors disabled:opacity-50 text-white"
                value={formData.phone}
                onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
              />
            </div>

            <div className="pt-4 border-t border-zinc-800 space-y-3">
               <button 
                  type="button"
                  disabled={status === 'submitting'}
                  onClick={handleToggleAll}
                  className="flex items-center gap-3 w-full text-left font-bold mb-2 disabled:opacity-50 group"
               >
                  <div className={`w-5 h-5 rounded flex items-center justify-center border transition-all ${allAgreed ? 'bg-[#FFC222] border-[#FFC222]' : 'border-zinc-700 group-hover:border-zinc-500'}`}>
                    {allAgreed && <Check size={14} className="text-black" />}
                  </div>
                  모두 동의합니다
               </button>

               {[
                 { key: 'agreePrivacy', label: '개인정보 수집 동의 (필수)' },
                 { key: 'agreeTerms', label: '서비스 이용약관 동의 (필수)' },
                 { key: 'agreeMarketing', label: '마케팅 정보 수신 동의 (선택)' },
               ].map((item) => (
                 <button 
                  key={item.key}
                  type="button"
                  disabled={status === 'submitting'}
                  onClick={() => setFormData(p => ({ ...p, [item.key]: !p[item.key as keyof typeof p] }))}
                  className="flex items-center gap-3 w-full text-left text-sm text-zinc-400 disabled:opacity-50 group py-1"
                 >
                   <div className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${formData[item.key as keyof typeof formData] ? 'bg-[#FFC222] border-[#FFC222]' : 'border-zinc-700 group-hover:border-zinc-500'}`}>
                    {formData[item.key as keyof typeof formData] && <Check size={12} className="text-black" />}
                   </div>
                   {item.label}
                 </button>
               ))}
            </div>

            {status === 'error' && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg border border-red-400/20">
                <AlertCircle size={16} />
                <span>전송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.</span>
              </div>
            )}

            <button 
              type="submit"
              disabled={status === 'submitting'}
              className={`w-full font-black py-4 rounded-xl transition-all flex items-center justify-center gap-2 mt-4 ${
                status === 'error' ? 'bg-red-500 text-white' : 'bg-[#FFC222] text-black hover:scale-[1.02] active:scale-[0.98]'
              } disabled:opacity-50 disabled:hover:scale-100 shadow-lg shadow-[#FFC222]/10`}
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  데이터 처리 중...
                </>
              ) : (
                '신청하기'
              )}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export const PolicyModal: React.FC<{ type: ModalType; onClose: () => void }> = ({ type, onClose }) => {
  if (!type || type === 'apply') return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/90 backdrop-blur-sm" 
        onClick={onClose} 
      />
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="relative w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-3xl p-8 md:p-10 max-h-[80vh] overflow-y-auto"
      >
        <button onClick={onClose} className="absolute top-6 right-6 text-zinc-500 hover:text-white transition-colors">
          <X size={24} />
        </button>
        <h3 className="text-2xl font-black mb-8">
          {type === 'privacy' ? '개인정보처리방침' : '서비스 이용약관'}
        </h3>
        <div className="text-zinc-400 space-y-6 leading-relaxed">
           <p className="font-bold text-white text-lg underline underline-offset-4 decoration-[#FFC222]">내용을 넣어주세요.</p>
           <p>본 약관은 ENACTUS YM에서 제공하는 서비스 및 지원 정보 수집과 관련한 기준을 정의합니다.</p>
           <p>ENACTUS YM은 지원자의 소중한 개인정보를 안전하게 관리하며, 수집된 정보는 선발 프로세스 종료 후 내부 규정에 따라 지체 없이 파기됩니다.</p>
           <p>1. 수집 항목: 성함, 휴대폰 번호<br/>2. 수집 목적: 17기 신입 부원 모집 및 선발 안내<br/>3. 보유 기간: 선발 종료 후 1개월 이내</p>
        </div>
      </motion.div>
    </div>
  );
};
